import { trpc } from 'api'
import { useCustomer } from 'hooks/useSession'
import { ErpTemple } from '@ca/common'
import { useCallback, useMemo } from 'react'

type UseTempleSearchParams = {
  query: string
  limit?: number
}

export function useTempleSearch({ query, limit = 20 }: UseTempleSearchParams) {
  const enabled = query.trim().length > 0
  const result = trpc.temples.list.useQuery({ query, limit }, { enabled })

  return result
}

export const useCustomerTemples = () => {
  const query = trpc.temples.customer.list.useInfiniteQuery(
    { limit: 10 },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.meta.nextCursor
      },
    }
  )

  return query
}

export const useSavedTemples = () => {
  const list = trpc.temples.customer.list.useQuery({ limit: 100 })

  const savedIds = useMemo(() => {
    const set = new Set<string>()
    const data = list.data?.data || []
    data.forEach((r) => set.add(String(r.temple_id)))
    return set
  }, [list.data])

  const isSaved = useCallback(
    (id: string | number) => savedIds.has(String(id)),
    [savedIds]
  )

  return {
    savedIds,
    isSaved,
  }
}

export function useTempleActions() {
  const customer = useCustomer()
  const utils = trpc.useUtils()

  const list = trpc.temples.customer.list.useQuery({ limit: 100 })

  const save = trpc.temples.customer.save.useMutation({
    onSuccess: () => {
      utils.temples.customer.list.invalidate()
    },
  })
  const remove = trpc.temples.customer.remove.useMutation({
    onSuccess: () => {
      utils.temples.customer.list.invalidate()
    },
  })

  const saveTemple = (params: ErpTemple) =>
    save.mutateAsync({
      ...params,
      temple_id: String(params.id),
      customer_id: String(customer!.id),
    })

  return {
    list,
    saveTemple,
    save,
    remove,
  }
}
